import React, { useState, useMemo } from "react";
import "./UpdatesPage.scss";

// --- RELEASE NOTES DATA ---
// Add new releases to the top of this array as they ship.
// Keep this in sync with `UPDATES_LIST` / `LATEST_UPDATE_VERSION` in Navbar.jsx
// so the red "new update" indicator and this page always agree.
//
// `type` controls the colored tag shown next to each item:
// "feature" | "improvement" | "performance" | "design" | "fix"
const RELEASES = [
  {
    version: "v2",
    date: "October 27, 2026",
    summary:
      "A major workspace refresh with navbar-led navigation, a compact full-height sidebar, consistent page layouts, and a guided introduction to the new navigation.",
    items: [
      {
        type: "feature",
        heading: "Module pages move into the navbar",
        body: "After you select a module such as ESS or Teams, its pages appear in the top navbar. Pages with submenus show those links in a second row.",
      },
      {
        type: "feature",
        heading: "A guided navigation tour",
        body: "A short animated walkthrough introduces the updated sidebar, module navigation, and submenu row the first time you enter the app. You can pause, step through, or skip it.",
      },
      {
        type: "improvement",
        heading: "Expanded and compact sidebar views",
        body: "The full-height sidebar shows your company logo and name with labeled navigation when expanded, and keeps the logo and icons available in its compact view.",
      },
      {
        type: "improvement",
        heading: "Consistent navigation across modules",
        body: "Email Automation follows the same navbar styling as ESS, with its module tabs shown in the top navigation. Coming Soon pages keep the navigation area clear.",
      },
      {
        type: "design",
        heading: "A more compact, consistent page layout",
        body: "ESS pages share a cleaner visual style, with page headings and action buttons arranged to make better use of the available space. Summary cards and status icons have also been refined.",
      },
      {
        type: "improvement",
        heading: "Clearer Teams navigation",
        body: "Teams now has its own network icon in the sidebar, making it easier to distinguish from ESS.",
      },
    ],
  },
  {
    version: "v1.1.1",
    date: "September 15, 2026",
    summary:
      "Small attendance calendar fixes for accurate weekly-off handling and Date of Joining based attendance data.",

    items: [
      {
        type: "fix",
        heading: "Attendance Calendar — Weekly Off & DOJ Fix",
        body: "Fixed an issue where Saturdays in August were incorrectly displayed as Absent and only appeared as Weekly Off after navigating to another month and returning. Weekly Off days now display consistently without requiring month navigation. Also fixed attendance data appearing before an employee's Date of Joining (DOJ). Pre-DOJ dates are now excluded from attendance data and statistics, preventing employees from showing attendance records before they joined the organisation.",
      },
    ],
  },

  {
    version: "v1.1.0",

    date: "June 30, 2026",

    summary:
      "A refreshed table experience, a cleaner interface,collapse sidebar with submenus and a more polished, enterprise-ready feel throughout the app.",

    items: [
      {
        type: "feature",
        heading: "Sidebar Updates",
        body: "Collapse sidebar with submenus which directly routes to that page.",
      },

      {
        type: "feature",
        heading: "Smart table sorting",
        body: "Click any column header to sort. Click again to reverse the order, or a third time to return to the default view.",
      },

      {
        type: "feature",
        heading: "Direct page navigation",
        body: "Jump straight to a specific page in long lists instead of clicking through one page at a time.",
      },

      {
        type: "improvement",
        heading: "Clearer sort indicators",
        body: "Arrows now show exactly which column is sorted and in which direction.",
      },

      {
        type: "improvement",
        heading: "Color-coded status badges",
        body: "Statuses like Approved, Pending, or Rejected are now instantly recognizable at a glance.",
      },

      {
        type: "improvement",
        heading: "Cleaner employee information layout",
        body: "Employee details are now presented in a more organized, easy-to-read format across all tables.",
      },

      {
        type: "improvement",
        heading: "More reliable row actions",
        body: "Action buttons inside tables no longer accidentally select or deselect a row.",
      },

      {
        type: "improvement",
        heading: "Row highlighting and selection",
        body: "Rows now support clear highlighting and selection, making it easier to track what you're working with.",
      },

      {
        type: "improvement",
        heading: "Improved search experience",
        body: "Searching feels faster and more intuitive, and you can now clear a search with a single click.",
      },

      {
        type: "improvement",
        heading: "Better empty and loading states",
        body: "Friendlier loading placeholders and clearer messages when there's no data to show.",
      },

      {
        type: "improvement",
        heading: "Improved text handling",
        body: "Long names or values are now handled more gracefully, keeping tables neat and readable.",
      },

      {
        type: "feature",
        heading: "Revamped sidebar",
        body: "The sidebar now supports collapsing and expanding to a compact, icon-only view, with flyout submenus for items that have nested links.",
      },

      {
        type: "improvement",
        heading: "Dynamic sidebar layout",
        body: "Collapsing or expanding the sidebar now resizes the main content area to match in real time, instead of leaving a stale gap or overlap.",
      },

      {
        type: "performance",
        heading: "Faster, smoother tables",
        body: "Sorting, searching, and paginating through large amounts of data feels noticeably quicker.",
      },

      {
        type: "performance",
        heading: "Smoother page transitions",
        body: "Moving between pages and screens feels smoother throughout the application.",
      },

      {
        type: "design",
        heading: "Refined enterprise look",
        body: "The overall interface has been polished for a more professional, modern appearance.",
      },

      {
        type: "design",
        heading: "Optional sticky first column",
        body: "Wide tables can keep key information, like employee name, visible while scrolling horizontally.",
      },

      {
        type: "design",
        heading: "Compact, information-dense layout",
        body: "Improved spacing fits more information on screen without feeling cluttered.",
      },

      {
        type: "design",
        heading: "Improved typography",
        body: "Refined font styling and sizing make text easier to read throughout the app.",
      },

      {
        type: "design",
        heading: "Cleaner navigation bar",
        body: "The top navigation bar has a tidier, more streamlined appearance.",
      },

      {
        type: "design",
        heading: "Softer visual depth",
        body: "Shadows and visual layers have been softened for a calmer, less distracting experience.",
      },

      {
        type: "design",
        heading: "Better responsiveness",
        body: "The application now looks better on large monitors and smaller laptop screens alike.",
      },

      {
        type: "design",
        heading: "Consistent scrollbars and borders",
        body: "Scrollbars, borders, and alignment are now consistent across the application.",
      },

      {
        type: "design",
        heading: "Improved hover effects",
        body: "Hovering over rows, buttons, and menus now gives clearer, more responsive feedback.",
      },

      {
        type: "fix",
        heading: "Row selection on action click",
        body: "Fixed an issue where clicking an action button inside a table could unintentionally select the row.",
      },

      {
        type: "fix",
        heading: "Spacing inconsistencies",
        body: "Resolved minor spacing inconsistencies across tables and menus.",
      },

      {
        type: "fix",
        heading: "Long value overflow",
        body: "Fixed display issues with long employee names or values overflowing table cells.",
      },

      {
        type: "fix",
        heading: "Sidebar submenus not appearing on hover",
        body: "Fixed flyout submenus failing to appear when hovering over a sidebar item with nested links.",
      },

      {
        type: "fix",
        heading: "Sidebar not expanding after collapse",
        body: "Fixed an issue where clicking to expand a collapsed sidebar had no effect, leaving it stuck at its narrow width.",
      },

      {
        type: "fix",
        heading: "Outdated sidebar submenu links",
        body: "Updated ESS, Teams, and Compensation/Benefits submenus so every item points to its correct page and is visible to the right roles.",
      },

      {
        type: "fix",
        heading: "Missing Admin section",
        body: "The Admin section, with Master, User Management, and Payroll settings, now appears in the sidebar for Admin and HR users.",
      },
    ],
  },
];

const TYPE_META = {
  feature: { label: "New" },
  improvement: { label: "Improved" },
  performance: { label: "Performance" },
  design: { label: "Design" },
  fix: { label: "Fixed" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "feature", label: "New" },
  { key: "improvement", label: "Improved" },
  { key: "performance", label: "Performance" },
  { key: "design", label: "Design" },
  { key: "fix", label: "Fixed" },
];

const UpdatesPage = () => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [activeVersion, setActiveVersion] = useState(RELEASES[0]?.version);

  const visibleReleases = useMemo(() => {
    if (activeFilter === "all") return RELEASES;
    return RELEASES.map((release) => ({
      ...release,
      items: release.items.filter((item) => item.type === activeFilter),
    })).filter((release) => release.items.length > 0);
  }, [activeFilter]);

  const handleJumpToVersion = (version) => {
    setActiveVersion(version);
    const el = document.getElementById(`release-${version}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="updates-page">
      <div className="updates-page-header">
        <h1>What's New</h1>
        <p>The latest improvements and capabilities in HRLabs.</p>

        <div className="updates-filters">
          {FILTERS.map((filter) => (
            <button
              key={filter.key}
              className={`filter-pill ${
                activeFilter === filter.key ? "active" : ""
              }`}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="updates-page-body">
        <aside className="updates-sidebar">
          <div className="sidebar-label">Versions</div>
          <nav className="sidebar-nav">
            {RELEASES.map((release) => (
              <button
                key={release.version}
                className={`sidebar-item ${
                  activeVersion === release.version ? "active" : ""
                }`}
                onClick={() => handleJumpToVersion(release.version)}
              >
                <span className="sidebar-version">{release.version}</span>
                <span className="sidebar-date">{release.date}</span>
              </button>
            ))}
          </nav>
        </aside>

        <main className="updates-content">
          {visibleReleases.length === 0 ? (
            <div className="updates-empty">
              <p>No updates match this filter yet.</p>
            </div>
          ) : (
            visibleReleases.map((release) => (
              <section
                className="release-block"
                id={`release-${release.version}`}
                key={release.version}
              >
                <div className="release-meta">
                  <span className="release-version">{release.version}</span>
                  <span className="release-date">{release.date}</span>
                </div>

                <p className="release-summary">{release.summary}</p>

                <div className="release-items">
                  {release.items.map((item, idx) => {
                    const meta = TYPE_META[item.type] || { label: "Update" };
                    return (
                      <div className="release-item" key={idx}>
                        <span className={`type-tag type-${item.type}`}>
                          {meta.label}
                        </span>
                        <div className="release-item-text">
                          <div className="item-heading">{item.heading}</div>
                          <p>{item.body}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </main>
      </div>
    </div>
  );
};

export default UpdatesPage;
